
// 🔹 React & Router imports
import { useState, useRef, useEffect, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import apiClient, { getClientId } from "../api";

// 🔹 Icons
import {
  FaUserCircle,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaBus,
} from "react-icons/fa";

// 🔹 Navbar Component
const Navbar = () => {
  const { user, logout, loading } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [navBusy, setNavBusy] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isConfirmBookingPage = location.pathname === "/confirm-booking";

  // ✅ Smooth hide/show navbar on scroll (no delay)
  const navRef = useRef(null);
  const [navHidden, setNavHidden] = useState(false);
  const lastYRef = useRef(0);
  const tickingRef = useRef(false);
  const scrollElRef = useRef(null);

  const readActiveLock = useCallback(() => {
    try {
      const raw = localStorage.getItem("rb_active_lock");
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (
        !parsed ||
        !parsed.busId ||
        !parsed.date ||
        !parsed.departureTime ||
        !Array.isArray(parsed.seats) ||
        parsed.seats.length === 0
      ) {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }, []);

  const releaseActiveLock = useCallback(async () => {
    const activeLock = readActiveLock();
    if (!activeLock) return;

    const authToken = localStorage.getItem("token") || null;

    try {
      await apiClient.delete("/bookings/release", {
        data: {
          busId: activeLock.busId,
          date: activeLock.date,
          departureTime: activeLock.departureTime,
          seats: activeLock.seats.map(String),
          clientId: getClientId(),
        },
        headers: authToken
          ? { Authorization: `Bearer ${authToken}` }
          : undefined,
      });
    } catch (err) {
      console.warn(
        "Navbar seat release failed:",
        err?.response?.data || err?.message || err
      );
    } finally {
      localStorage.removeItem("rb_active_lock");
      sessionStorage.removeItem("rb_skip_release_on_unmount");
      sessionStorage.removeItem("rb_restore_payload");
      sessionStorage.removeItem("rb_confirm_draft");
      sessionStorage.setItem(
        "rb_lock_notice",
        "Your held seats were released because you left the booking page."
      );
    }
  }, [readActiveLock]);

  const guardedNavigate = useCallback(
    async (to, options = {}) => {
      const { afterNavigate } = options;

      if (navBusy) return;

      const activeLock = readActiveLock();

      if (activeLock) {
        const ok = window.confirm(
          "Leave this page? Your held seats will be released."
        );
        if (!ok) return;

        setNavBusy(true);
        try {
          await releaseActiveLock();
        } finally {
          setNavBusy(false);
        }
      }

      if (typeof afterNavigate === "function") {
        afterNavigate();
      }

      const isFinalPage =
        location.pathname.startsWith("/download-ticket") ||
        location.pathname.startsWith("/payment-return");

      navigate(to, isFinalPage ? { replace: true } : {});
    },
    [navBusy, navigate, readActiveLock, releaseActiveLock, location.pathname]
  );

  const guardedClick = useCallback(
    (to, options = {}) => async (e) => {
      e.preventDefault();
      await guardedNavigate(to, options);
    },
    [guardedNavigate]
  );

  // 🔹 Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Find the actual scroll container (some layouts don't scroll on window)
  useEffect(() => {
    const isScrollable = (el) => {
      if (!el) return false;
      const style = window.getComputedStyle(el);
      const oy = style.overflowY;
      const canScroll = (oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight;
      return canScroll;
    };

    // try common containers in order
    const candidates = [
      document.scrollingElement,
      document.documentElement,
      document.body,
      document.getElementById("root"),
      document.querySelector("main"),
      document.querySelector("[data-scroll-container]"),
    ].filter(Boolean);

    // pick the first truly scrollable element, else fallback to document.scrollingElement
    const picked = candidates.find(isScrollable) || document.scrollingElement || document.documentElement;

    scrollElRef.current = picked;
    lastYRef.current = picked?.scrollTop || 0;
  }, []);

  // ✅ Expose navbar height (useful for sticky search card alignment)
  useEffect(() => {
    const setNavHeight = () => {
      const h = navRef.current?.offsetHeight || 0;
      document.documentElement.style.setProperty("--rb-nav-h", `${h}px`);
    };
    setNavHeight();
    window.addEventListener("resize", setNavHeight);
    return () => window.removeEventListener("resize", setNavHeight);
  }, []);

  // ✅ Hide/show on scroll (works for window-scroll AND container-scroll)
  useEffect(() => {
    const el = scrollElRef.current || document.scrollingElement || document.documentElement;
    if (!el) return;

    const getY = () => el.scrollTop || 0;

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;

      requestAnimationFrame(() => {
        const y = getY();
        const last = lastYRef.current;

        const goingDown = y > last + 6;
        const goingUp = y < last - 6;

        if (y < 30) {
          setNavHidden(false);
        } else if (goingDown) {
          setNavHidden(true);
          setDropdownOpen(false);
          // don’t force-close the mobile drawer if user opened it intentionally
        } else if (goingUp) {
          setNavHidden(false);
        }

        lastYRef.current = y;
        tickingRef.current = false;
      });
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // 🔹 Active link styling
  const getLinkStyle = ({ isActive }) =>
    `relative px-3 py-2 text-sm font-medium transition-colors duration-300 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-blue-50${
      isActive ? " text-blue-600 bg-blue-50 font-semibold" : ""
    }`;

  if (loading) return null;

  const isLoggedIn = !!user;
  const roleStr = user?.role?.toString?.().toLowerCase?.() || "";
  const isAdmin =
    roleStr === "admin" ||
    user?.isAdmin === true ||
    (Array.isArray(user?.roles) &&
      user.roles.some((r) => r?.toString?.().toLowerCase?.() === "admin"));
  const isOperator =
    roleStr === "operator" ||
    (Array.isArray(user?.roles) &&
      user.roles.some((r) => r?.toString?.().toLowerCase?.() === "operator"));

  return (
    <>
      {!isConfirmBookingPage && (
        <>
          {/* ✅ Spacer (prevents layout jump when navbar is fixed) */}
          <div style={{ height: "var(--rb-nav-h, 0px)" }} />

          {/* ✅ Fixed wrapper so hide/show works reliably */}
          <div
            ref={navRef}
            className={[
              "fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100",
              "transition-transform duration-150 ease-out will-change-transform",
              navHidden ? "-translate-y-full" : "translate-y-0",
            ].join(" ")}
          >
            {/* 🔹 Desktop Navbar */}
            <nav
              key={user?._id || "guest"}
              className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center md:flex hidden"
            >
              {/* Logo */}
              <NavLink
                to="/"
                onClick={guardedClick("/")}
                className="flex items-center gap-2 text-2xl font-bold transition-all duration-300"
              >
                {/* removed small bus icon */}
                <span className="routesbook-brand text-[#E11D48]"><span className="routesbook-brand text-[#E11D48]">Routesbook</span></span>
              </NavLink>

              {/* Mobile toggle button (hidden on desktop) */}
              <div className="md:hidden">
                <button onClick={() => setMenuOpen(true)} className="p-2 rounded-md">
                  <FaBars className="text-gray-800" size={22} />
                </button>
              </div>

              {/* Links & User Section */}
              <div className="hidden md:flex items-center gap-2">
                <ul className="flex items-center gap-2">
                  <li>
                    <NavLink to="/" onClick={guardedClick("/")} className={getLinkStyle}>
                      Home
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/help"
                      onClick={guardedClick("/help")}
                      className={getLinkStyle}
                    >
                      Help
                    </NavLink>
                  </li>

                  {isLoggedIn && (
                    <li>
                      <NavLink
                        to="/my-bookings"
                        onClick={guardedClick("/my-bookings")}
                        className={getLinkStyle}
                      >
                        My Bookings
                      </NavLink>
                    </li>
                  )}

                  {isOperator && (
                    <li>
                      <NavLink
                        to="/operator/dashboard"
                        onClick={guardedClick("/operator/dashboard")}
                        className={getLinkStyle}
                      >
                        Operator Dashboard
                      </NavLink>
                    </li>
                  )}

                  {isAdmin && (
                    <li>
                      <NavLink
                        to="/admin"
                        onClick={guardedClick("/admin")}
                        className={getLinkStyle}
                      >
                        Admin
                      </NavLink>
                    </li>
                  )}
                </ul>

                {/* Divider */}
                <div className="w-px h-6 mx-2 bg-gray-200" />

                {/* User dropdown */}
                {isLoggedIn ? (
                  <div ref={dropdownRef} className="relative">
                    <button
                      onClick={() => setDropdownOpen((p) => !p)}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-gray-700 hover:bg-gray-100"
                      disabled={navBusy}
                    >
                      <FaUserCircle size={20} />
                      <span className="text-sm font-medium">
                        {user?.name || "Account"}
                      </span>
                      <FaChevronDown
                        size={12}
                        className={`transition-transform ${
                          dropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {dropdownOpen && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border z-10">
                        <div className="p-2">
                          <button
                            onClick={() =>
                              guardedNavigate("/profile", {
                                afterNavigate: () => setDropdownOpen(false),
                              })
                            }
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-60"
                            disabled={navBusy}
                          >
                            Profile
                          </button>
                          <hr className="my-1" />
                          <button
                            onClick={async () => {
                              if (navBusy) return;

                              const activeLock = readActiveLock();
                              if (activeLock) {
                                const ok = window.confirm(
                                  "Leave this page? Your held seats will be released."
                                );
                                if (!ok) return;

                                setNavBusy(true);
                                try {
                                  await releaseActiveLock();
                                } finally {
                                  setNavBusy(false);
                                }
                              }

                              logout();
                              setDropdownOpen(false);

                              const isFinalPage =
                                location.pathname.startsWith("/download-ticket") ||
                                location.pathname.startsWith("/payment-return");

                              navigate("/", isFinalPage ? { replace: true } : {});
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-red-500 rounded-md hover:bg-red-50 disabled:opacity-60"
                            disabled={navBusy}
                          >
                            Sign Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Guest view (Login / Signup)
                  <div className="flex items-center gap-2">
                    <NavLink
                      to="/login"
                      onClick={guardedClick("/login")}
                      className="px-4 py-1.5 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100"
                    >
                      Login
                    </NavLink>
                    <NavLink
                      to="/signup"
                      onClick={guardedClick("/signup")}
                      className="px-4 py-1.5 text-sm font-medium rounded-md shadow-sm hover:shadow-md bg-blue-600 text-white"
                    >
                      Sign Up
                    </NavLink>
                  </div>
                )}
              </div>
            </nav>
          </div>

          {/* 🔹 Mobile Menu */}
          <div
            className={`fixed inset-0 z-[100] md:hidden transition-transform duration-300 ${
              menuOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Overlay */}
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setMenuOpen(false)}
            />

            {/* Sidebar Menu */}
            <div className="relative w-full max-w-xs h-full bg-white ml-auto flex flex-col justify-between py-6 px-4">
              {/* Header */}
              <div>
                <div className="flex justify-between items-center mb-6">
                  <button
                    onClick={() =>
                      guardedNavigate("/", {
                        afterNavigate: () => setMenuOpen(false),
                      })
                    }
                    className="text-lg font-bold"
                    disabled={navBusy}
                  >
                    Routesbook
                  </button>
                  <button onClick={() => setMenuOpen(false)} disabled={navBusy}>
                    <FaTimes size={20} />
                  </button>
                </div>

                {/* Mobile Links */}
                <div className="flex flex-col gap-2">
                  <NavLink
                    to="/"
                    onClick={guardedClick("/", {
                      afterNavigate: () => setMenuOpen(false),
                    })}
                    className="p-3 text-base font-medium text-gray-700 rounded-lg hover:bg-gray-100 text-left"
                  >
                    Home
                  </NavLink>

                  <NavLink
                    to="/help"
                    onClick={guardedClick("/help", {
                      afterNavigate: () => setMenuOpen(false),
                    })}
                    className="p-3 text-base font-medium text-gray-700 rounded-lg hover:bg-gray-100 text-left"
                  >
                    Help
                  </NavLink>

                  {isLoggedIn && (
                    <NavLink
                      to="/my-bookings"
                      onClick={guardedClick("/my-bookings", {
                        afterNavigate: () => setMenuOpen(false),
                      })}
                      className="p-3 text-base font-medium text-gray-700 rounded-lg hover:bg-gray-100 text-left"
                    >
                      My Bookings
                    </NavLink>
                  )}

                  {isOperator && (
                    <NavLink
                      to="/operator/dashboard"
                      onClick={guardedClick("/operator/dashboard", {
                        afterNavigate: () => setMenuOpen(false),
                      })}
                      className="p-3 text-base font-medium text-gray-700 rounded-lg hover:bg-gray-100 text-left"
                    >
                      Operator Dashboard
                    </NavLink>
                  )}

                  {isAdmin && (
                    <NavLink
                      to="/admin"
                      onClick={guardedClick("/admin", {
                        afterNavigate: () => setMenuOpen(false),
                      })}
                      className="p-3 text-base font-medium text-gray-700 rounded-lg hover:bg-gray-100 text-left"
                    >
                      Admin
                    </NavLink>
                  )}
                </div>
              </div>

              {/* Footer actions */}
              <div className="flex flex-col gap-2">
                {isLoggedIn ? (
                  <>
                    <hr className="my-2" />
                    <button
                      onClick={() =>
                        guardedNavigate("/profile", {
                          afterNavigate: () => setMenuOpen(false),
                        })
                      }
                      className="p-3 text-base font-medium text-gray-700 rounded-lg hover:bg-gray-100 text-left disabled:opacity-60"
                      disabled={navBusy}
                    >
                      Profile
                    </button>
                    <button
                      onClick={async () => {
                        if (navBusy) return;

                        const activeLock = readActiveLock();
                        if (activeLock) {
                          const ok = window.confirm(
                            "Leave this page? Your held seats will be released."
                          );
                          if (!ok) return;

                          setNavBusy(true);
                          try {
                            await releaseActiveLock();
                          } finally {
                            setNavBusy(false);
                          }
                        }

                        logout();
                        setMenuOpen(false);

                        const isFinalPage =
                          location.pathname.startsWith("/download-ticket") ||
                          location.pathname.startsWith("/payment-return");

                        navigate("/", isFinalPage ? { replace: true } : {});
                      }}
                      className="p-3 text-base font-medium text-red-500 rounded-md hover:bg-red-50 text-left disabled:opacity-60"
                      disabled={navBusy}
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <hr className="my-2" />
                    <NavLink
                      to="/login"
                      onClick={guardedClick("/login", {
                        afterNavigate: () => setMenuOpen(false),
                      })}
                      className="p-3 text-base font-medium text-gray-700 rounded-lg hover:bg-gray-100"
                    >
                      Login
                    </NavLink>
                    <NavLink
                      to="/signup"
                      onClick={guardedClick("/signup", {
                        afterNavigate: () => setMenuOpen(false),
                      })}
                      className="p-3 text-base font-medium text-center text-white bg-blue-600 rounded-lg"
                    >
                      Sign Up
                    </NavLink>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;

